import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, CanMatchFn, Router, Route, UrlSegment, UrlTree } from '@angular/router';
import { AuthService } from '../../../../../libs/core';
import { SuperadminAuthService } from './auth.service';

const SIDEBAR_ROUTE_PERMISSIONS: ReadonlyArray<{ path: string; permission: string }> = [
  { path: '/dashboard', permission: 'Dashboard.Read' },
  { path: '/tenant', permission: 'TenantManagement.Read' },
  { path: '/user-management', permission: 'UserManagement.Read' },
  { path: '/notification', permission: 'NotificationManagement.Read' },
  { path: '/support', permission: 'SupportManagement.Read' },
];

function requireAuth(): boolean | UrlTree {
  const authService = inject(SuperadminAuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    return true;
  }

  return router.parseUrl('/login');
}

function requireGuest(): boolean | UrlTree {
  const authService = inject(SuperadminAuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return true;
  }

  return router.parseUrl(authService.getLastProtectedRoute());
}

function getFallbackAuthorizedRoute(): string {
  const authService = inject(AuthService);

  for (const item of SIDEBAR_ROUTE_PERMISSIONS) {
    if (authService.hasPermission(item.permission)) {
      return item.path;
    }
  }

  return '/login';
}

function routeMatchesPrefix(routePath: string | undefined, targetPrefix: string): boolean {
  if (!routePath) {
    return false;
  }

  return routePath === targetPrefix || routePath.startsWith(`${targetPrefix}/`);
}

function readPermissionFromRoute(route: Route): string | undefined {
  const explicitPermission = route.data?.['permission'];
  if (typeof explicitPermission === 'string' && explicitPermission.trim()) {
    return explicitPermission;
  }

  const routePath = route.path;
  if (routeMatchesPrefix(routePath, 'tenant')) {
    return 'TenantManagement.Read';
  }

  if (routeMatchesPrefix(routePath, 'user-management')) {
    return 'UserManagement.Read';
  }

  if (routeMatchesPrefix(routePath, 'notification')) {
    return 'NotificationManagement.Read';
  }

  if (routeMatchesPrefix(routePath, 'support')) {
    return 'SupportManagement.Read';
  }

  if (routeMatchesPrefix(routePath, 'dashboard')) {
    return 'Dashboard.Read';
  }

  return undefined;
}

function requirePermission(permission: string | undefined): boolean | UrlTree {
  if (!permission) {
    return true;
  }

  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.hasPermission(permission)) {
    return true;
  }

  return router.parseUrl(getFallbackAuthorizedRoute());
}

export const superadminPermissionCanActivate: CanActivateFn = (route) => {
  const authenticated = requireAuth();
  if (authenticated !== true) {
    return authenticated;
  }

  return requirePermission(route.data?.['permission'] as string | undefined);
};

export const superadminPermissionCanMatch: CanMatchFn = (route: Route, _segments: UrlSegment[]) => {
  const authenticated = requireAuth();
  if (authenticated !== true) {
    return authenticated;
  }

  return requirePermission(readPermissionFromRoute(route));
};

export const superadminAuthCanMatch: CanMatchFn = (_route: Route, segments: UrlSegment[]) => {
  const firstSegment = segments[0]?.path?.toLowerCase();
  if (firstSegment === 'error') {
    return false;
  }

  return requireAuth();
};
export const superadminAuthCanActivateChild: CanActivateChildFn = () => requireAuth();
export const superadminGuestCanMatch: CanMatchFn = () => requireGuest();
export const superadminGuestCanActivate: CanActivateFn = () => requireGuest();
